//TODO: This will need to be merged with quick enrol widget 
import { Form, Formik } from "formik";
import React, { FC, useEffect, useState } from "react";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { STANDARD_NUMBER_OF_BITS } from "../../../constants/credential-constants";
import settingTypesBasicFields from "../../../constants/setting-types-basic-fields";
import TagStatus from '../../../constants/tag-status';
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { CategoryFieldGroup } from "../../../models/CategoryFieldGroup";
import { Tag } from "../../../models/Tag";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { getAccessGroupsForPerson } from "../../../services/accessGroupService";
import { saveCredential } from "../../../services/credentialsService";
import { storePerson } from "../../../services/enrolmentService";
import { assignUnassignMasterGroup } from "../../../services/masterGroupService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { buildCardData } from "../../../utilities/CredentialUtil";
import { appendFilter } from "../../../utilities/filters";
import { useDialog } from "../../dialog/DialogContext";
import DetailsForm from "../../enroll-credentials/details-form";
import WizardTwo from "../../wizard/wizardTwo";
import QuickEnrolSummary from "./add/summary";
import TypeSelectionStep from "./add/type-selection-step";
import UserDetailsStep from "./add/user-details-step";
import { toast } from "react-toastify";
import GroupStep from "./add/group-step";
import { LICENSE_APERIO_OSS } from "../../../constants/license-constants";
import { useGlobalState } from "../../../Providers";

interface Props {
    transaction: any;
    isOpen: boolean;
    onClose: () => void;
}

const TransactionQuickEnrollModal: FC<Props> = ({ transaction, isOpen, onClose }) => {
    const { buildInfo } = useGlobalState() ?? {};
    const isLicensedForAperioOss = buildInfo?.licenceIds?.includes(LICENSE_APERIO_OSS);
    const { translate } = useLocale();
    const dialog = useDialog();
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [supportedFields, setSupportedFields] = useState<CategoryFieldGroup[]>([]);
    const [accessGroupField, setAccessGroupField] = useState<boolean>(false);
    const [tagsField, setTagsField] = useState<boolean>(false);
    const [credentialType, setCredentialType] = useState<string>("");
    const [taskProcedure, setTaskProcedure] = useState<string>("");

    const getInitialValues = () => {
        const tagCode = transaction?.tagCode || "";
        
        return {
            id: 0,
            code: tagCode,
            tagDisplay: tagCode,
            type: '',
            typeId: '',
            codeUntruncated: tagCode,
            startDate: undefined,
            startTime: '',
            expiryDate: undefined,
            expiryTime: '',
            statusId: '1',
            firstName: '',
            lastName: '',
            name: ''
        };
    };

    const [step, setStep] = useState(0);
    const [initialValues, setInitialValues] = useState(getInitialValues());
    const [cardValues, setCardValues] = useState<Tag>(getInitialValues());

    useEffect(() => {
        if (transaction?.tagCode) {
            const tagCode = transaction.tagCode;
            const prefilledCardValues: Tag = {
                ...cardValues,
                code: tagCode,
                tagDisplay: tagCode,
                codeUntruncated: tagCode,
                statusId: TagStatus.Normal.value,
                numberOfBits: STANDARD_NUMBER_OF_BITS,
                typeId: '1',
            };
            setCardValues(prefilledCardValues);
        }
    }, [transaction]);

    useEffect(() => {
        if (transaction) {
            const newValues = getInitialValues();
            setInitialValues(newValues);
        }
    }, [transaction]);

    const initialSteps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.type),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.userDetails),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
    ];

    const buildMaster = (values: any) => ({
        profileId: values.categoryTypeId,
        name: values.name,
        selectedImage: values.selectedImage,
        email: values.email,
        uid: values.uid,
        pin: values.pin,
        enableCredentials: true,
        apbOverride: values.apbOverride,
        mobileNumber: values.mobileNumber,
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName,
        title: values.title,
        suffix: values.suffix,
        gender: values.gender,
        genderLabel: values.genderLabel,
        printTemplate: values.printTemplate,
        timeAndAttendance: values.timeAndAttendance,
        company: values.company,
        department: values.department,
        siteId: siteId,
        description: values.description,
        images: [],
        profileName: "",
        birthday: undefined,
        birthdayId: 0,
        id: 0,
        totalTags: 0,
        totalAccessGroups: 0,
        totalFloors: 0,
        totalOssGroups: 0,
        totalAssets: 0,
        custom: values.custom
    });

    useEffect(() => {
        if (supportedFields.length === 0) {
            setSideBarSteps(initialSteps);
        } else {
            const agField = supportedFields.some(data =>
                data.fields.some(field => field.columnId === settingTypesBasicFields.Access_Groups.id)
            );
            const credentialsField = supportedFields.some(data =>
                data.fields.some(field => field.columnId === settingTypesBasicFields.Tags.id)
            );
            
            //only set credentialType if we support credintials
            if (credentialsField) {
                setCredentialType(translate.byKey(TranslationKeys.enrolCard));
                setTaskProcedure(translate.byKey(TranslationKeys.manualEnterCodeType));
            }

            const updatedSteps: WizardSideBarStep[] = [
                sideBarSteps[0],
                sideBarSteps[1],
                ...(credentialsField ? [{
                    title: translate.byKey(TranslationKeys.credential),
                    description: translate.byKey(TranslationKeys.pending),
                    substeps: []
                }] : []),
                ...(agField ? [{
                    title: translate.byKey(TranslationKeys.accessGroups),
                    description: translate.byKey(TranslationKeys.pending),
                    substeps: []
                }] : []),
                ...(agField ? [{
                    title: translate.byKey(TranslationKeys.floorGroups),
                    description: translate.byKey(TranslationKeys.pending),
                    substeps: []
                }] : []),
                ...(agField && isLicensedForAperioOss ? [{
                    title: translate.byKey(TranslationKeys.ossGroups),
                    description: translate.byKey(TranslationKeys.pending),
                    substeps: []
                }] : []),
                {
                    title: translate.byKey(TranslationKeys.summary),
                    description: translate.byKey(TranslationKeys.pending),
                    substeps: []
                },
            ];

            setAccessGroupField(agField);
            setTagsField(credentialsField);
            setSideBarSteps(updatedSteps);
        }
    }, [supportedFields]);

    const [sideBarSteps, setSideBarSteps] = useState<WizardSideBarStep[]>(initialSteps);

    const onLocalNext = () => {
        setStep(step + 1);
    };

    const onLocalBack = () => {
        if (step > 0) {
            setStep(step - 1);
        }
    };

    const setSideBarStepDescription = (index, newDescription) => {
        const newSideBarSteps = sideBarSteps.map((step, stepIndex) => {
            return { ...step, description: stepIndex === index ? newDescription : step.description };
        });
        setSideBarSteps(newSideBarSteps);
    };

    const storeDetails = async (values: any) => {
        try {
            const masterDto = buildMaster(values);
            const masterData = (await storePerson(masterDto));
            const toBeAssignedAccessGroups = values.accessGroupsIds;
            const toBeAssignedFloorGroups = values.floorGroupsIds;
            const toBeAssignedOssGroups = values.ossGroupsIds;

            if (tagsField && cardValues.code) {
                const newCard = {
                    ...values,
                    masterId: masterData.id,
                    numberOfBits: values.numberOfBits || STANDARD_NUMBER_OF_BITS,
                    isCodeModified: false
                }

                const cardInfo = buildCardData(newCard, translate);
                await saveCredential(cardInfo);
            }
            if (accessGroupField) {
                const masterGroup = (await getAccessGroupsForPerson(translate, { filters: appendFilter(masterData.id, 'Master', 'master.id', []) })).rows;
                const selectedIds = masterGroup.map(accessGroup => accessGroup.id.toString())

                if (toBeAssignedAccessGroups?.length > 0) {
                    const toBeAdded = toBeAssignedAccessGroups.filter((value: string) => !selectedIds.includes(value));
                    await assignUnassignMasterGroup(toBeAdded, [], masterData.id);
                }

                if (toBeAssignedFloorGroups?.length > 0) {
                    const toBeAdded = toBeAssignedFloorGroups.filter((value: string) => !selectedIds.includes(value));
                    await assignUnassignMasterGroup(toBeAdded, [], masterData.id);
                }

                if (toBeAssignedOssGroups?.length > 0) {
                    const toBeAdded = toBeAssignedOssGroups.filter((value: string) => !selectedIds.includes(value));
                    await assignUnassignMasterGroup(toBeAdded, [], masterData.id);
                }
            }

            toast.success(translate.byKey(TranslationKeys.success));
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            handleClose();
        }
    }

    const getContent = () => {
        return [
            <TypeSelectionStep
                onBack={onLocalBack}
                onNext={onLocalNext}
                setSideBarStepDescription={newDescription => setSideBarStepDescription(step, newDescription)}
            />,
            <UserDetailsStep
                onBack={onLocalBack}
                onNext={onLocalNext}
                setSideBarStepDescription={newDescription => setSideBarStepDescription(step, newDescription)}
                supportedFields={supportedFields}
                setSupportedFields={setSupportedFields}
            />,
          ...(tagsField ? [
                <DetailsForm
                    onNext={() => {
                        setSideBarStepDescription(step, translate.byKey(TranslationKeys.details));
                        onLocalNext();
                    }}
                    onBack={onLocalBack}
                    cardInfo={cardValues}
                />
            ] : []),
            ...(accessGroupField ? [
                <GroupStep
                    key={"accessGroupStep"}
                    onBack={onLocalBack}
                    onNext={onLocalNext}
                    setSideBarStepDescription={newDescription => setSideBarStepDescription(step, newDescription)}
                    groupType={1}/>,
                <GroupStep
                    key={"floorGroupStep"}
                    onBack={onLocalBack}
                    onNext={onLocalNext}
                    setSideBarStepDescription={newDescription => setSideBarStepDescription(step, newDescription)}
                    groupType={2}/>,
                ...(isLicensedForAperioOss ? [<GroupStep
                    key={"ossGroupStep"}
                    onBack={onLocalBack}
                    onNext={onLocalNext}
                    setSideBarStepDescription={newDescription => setSideBarStepDescription(step, newDescription)}
                    groupType={3} />] : [])
            ] : []),
            <QuickEnrolSummary
                onBack={onLocalBack}
                setSideBarStepDescription={newDescription => setSideBarStepDescription(step, newDescription)}
                credentialType={credentialType}
                taskProcedure={taskProcedure}
            />
        ];
    }

    const handleClose = () => {
        setStep(0);
        setSideBarSteps(initialSteps);
        setSupportedFields([]);
        setCardValues(getInitialValues());
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            ariaHideApp={false}
            overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
            className="bg-white border-none m-auto shadow select-none outline-none w-content"
        >
            <Formik 
                initialValues={initialValues} 
                onSubmit={storeDetails}
                enableReinitialize={true}
            >
                <Form>
                    <WizardTwo
                        onClose={handleClose}
                        step={step}
                        name={translate.byKey(TranslationKeys.quickEnrol)}
                        sidebarSteps={sideBarSteps}
                        stepContent={getContent()}
                    />
                </Form>
            </Formik>
        </Modal>
    );
};

export default TransactionQuickEnrollModal;